import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, getAuthHeaders } from '../utils/auth';
import { API_ENDPOINTS } from '../config/api';
import './CurrentUserDetails.css';

function CurrentUserDetails() {
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [pageSizeInput, setPageSizeInput] = useState('10');
  const [searchQuery, setSearchQuery] = useState('');

  // Handle resume download
  const handleDownloadResume = async (resumeId, userName) => {
    try {
      const response = await fetch(API_ENDPOINTS.RESUMES.ADMIN_DOWNLOAD(resumeId), {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders({ 'Accept': 'application/pdf,application/octet-stream' }),
      });

      if (!response.ok) {
        throw new Error(`Failed to download resume: ${response.status}`);
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${userName || 'user'}_resume.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading resume:', err);
      alert('Failed to download resume: ' + err.message);
    }
  };

  // Check authentication
  useEffect(() => {
    const checkAuth = () => {
      if (!isAuthenticated()) {
        setTimeout(() => {
          if (!isAuthenticated()) {
            navigate('/signup');
          } else {
            setAuthChecked(true);
          }
        }, 100);
      } else {
        setAuthChecked(true);
      }
    };
    checkAuth();
  }, [navigate]);

  // Fetch user details from API
  useEffect(() => {
    if (!authChecked) return;

    const fetchUserDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const headers = getAuthHeaders();

        const response = await fetch(`${API_ENDPOINTS.USERS.BASE}/current-details?page=${page}&size=${size}`, {
          method: 'GET',
          headers,
          credentials: 'include',
        });

        if (response.status === 403) {
          throw new Error('No access - Admin privileges required');
        }

        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }

        const data = await response.json();
        console.log('📊 [CurrentUserDetails] API Response:', data);
        console.log('📊 [CurrentUserDetails] Total users:', data.totalElements || data.total);
        console.log('📊 [CurrentUserDetails] Users array:', data.users);
        
        // Normalize response structure to match expected format
        const normalizedData = {
          total: data.totalElements || data.total || 0,
          users: data.users || [],
          totalPages: data.totalPages,
          currentPage: data.currentPage,
          pageSize: data.pageSize,
          first: data.first,
          last: data.last
        };
        
        setUserDetails(normalizedData);
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError(err.message || 'Failed to fetch user details');
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [authChecked, page, size]);

  if (!authChecked || loading) {
    return (
      <div className="user-details-page">
        <div className="user-details-container">
          <div className="loading-spinner">Loading user details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-details-page">
        <div className="user-details-container">
          <div className="error-message">
            <h2>Error</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-details-page">
      <div className="user-details-container">
        <div className="user-details-header">
          <h1>Current User Details</h1>
          <p className="admin-note">Admin View - Internal Use Only</p>
        </div>

        {userDetails && (
          <div className="user-details-content">
            <div className="stats-summary">
              <div className="stat-item">
                <span className="stat-label">Total Users:</span>
                <span className="stat-value">{userDetails.total || 0}</span>
              </div>
            </div>

            <div className="search-bar">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="search-input"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="search-clear" onClick={() => setSearchQuery('')}>✕</button>
              )}
            </div>

            {(() => {
              const filteredUsers = searchQuery.trim()
                ? (userDetails.users || []).filter(user => {
                    const q = searchQuery.toLowerCase();
                    return (user.name || '').toLowerCase().includes(q) ||
                           (user.email || '').toLowerCase().includes(q);
                  })
                : userDetails.users || [];

              return filteredUsers.length > 0 ? (
              <>
                <div className="table-wrapper">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>SL No</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Last Login</th>
                        <th>Total Credits</th>
                        <th>Resume</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user, index) => (
                        <tr key={user.email || index}>
                          <td>{page * size + index + 1}</td>
                          <td>{user.name || 'N/A'}</td>
                          <td>{user.email || 'N/A'}</td>
                          <td>{user.lastLoginAt || 'Never'}</td>
                          <td className="credits-cell">{user.totalCredits !== null && user.totalCredits !== undefined ? user.totalCredits.toFixed(1) : '0.0'}</td>
                          <td>
                            <button 
                              className="btn-download-resume"
                              onClick={() => handleDownloadResume(user.resumeId, user.name)}
                              title="Download Resume"
                              disabled={!user.resumeId}
                            >
                              ⬇️ Download
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pagination-controls">
                  <button 
                    className="btn-pagination"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0 || loading}
                  >
                    ← Previous
                  </button>
                  
                  <div className="page-size-control">
                    <label htmlFor="pageSize">Page Size:</label>
                    <input
                      id="pageSize"
                      type="number"
                      className="page-size-input"
                      value={pageSizeInput}
                      min={1}
                      max={1000}
                      onChange={(e) => setPageSizeInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const val = parseInt(pageSizeInput, 10);
                          if (val > 0 && val <= 1000) {
                            setSize(val);
                            setPage(0);
                          } else {
                            setPageSizeInput(String(size));
                          }
                        }
                      }}
                      onBlur={() => {
                        const val = parseInt(pageSizeInput, 10);
                        if (!val || val < 1 || val > 1000) {
                          setPageSizeInput(String(size));
                        }
                      }}
                    />
                    <span className="page-size-hint">Press Enter</span>
                  </div>

                  <span className="pagination-info">
                    Showing {page * size + 1} - {Math.min((page + 1) * size, userDetails.total)} of {userDetails.total}
                  </span>
                  
                  <button 
                    className="btn-pagination"
                    onClick={() => setPage(p => p + 1)}
                    disabled={loading || userDetails.last || (page + 1) * size >= userDetails.total}
                  >
                    Next →
                  </button>
                </div>
              </>
            ) : (
              <div className="no-data">{searchQuery ? `No users found matching "${searchQuery}"` : 'No users found'}</div>
            );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

export default CurrentUserDetails;
